import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import CameraLoader from "./components/CameraLoader";
import API_BASE_URL from "./config";
import "./EditProfile.css";

function EditProfile() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);

  const [form, setForm] = useState({
    username: "",
    email: "",
    studio_name: "",
    phone_number: "",
    address: "",
    city: "",
    state: "",
    country: "",
    postal_code: "",
    bio: "",
    gstin: "",
    currency: "INR",
  });

  const userId = localStorage.getItem("userId");

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/user/${userId}`);
      if (!res.ok) throw new Error("Failed to load profile");
      const data = await res.json();
      const u = data.user || data;
      setForm({
        username: u.username || "",
        email: u.email || "",
        studio_name: u.studio_name || "",
        phone_number: u.phone_number || "",
        address: u.address || "",
        city: u.city || "",
        state: u.state || "",
        country: u.country || "",
        postal_code: u.postal_code || "",
        bio: u.bio || "",
        gstin: u.gstin || "",
        currency: u.currency || "INR",
      });
      if (u.logo_key || u.logo_url) {
        setLogoPreview(`${API_BASE_URL}/api/user/${userId}/logo`);
      }
    } catch (err) {
      setError("Could not load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("Logo must be under 5 MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;
      setLogoPreview(base64);

      try {
        const res = await fetch(`${API_BASE_URL}/api/user/${userId}/logo`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            logo: base64,
            contentType: file.type,
          }),
        });
        if (!res.ok) throw new Error("Upload failed");
        setSuccess("Logo uploaded!");
        setTimeout(() => setSuccess(""), 3000);
      } catch {
        setError("Failed to upload logo");
        setTimeout(() => setError(""), 4000);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/user/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Save failed");
      setSuccess("Profile saved!");
      setTimeout(() => setSuccess(""), 3000);
    } catch {
      setError("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ep-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="ep-main-content">
        <header className="ep-header">
          <div className="ep-header-left">
            <button
              className="ep-toggle-sidebar"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              ☰
            </button>
            <h1 className="ep-page-title">Edit Profile</h1>
          </div>
          <div className="ep-header-right">
            <button
              className="ep-back-btn"
              onClick={() => navigate("/dashboard")}
            >
              ← Dashboard
            </button>
          </div>
        </header>

        <main className="ep-main">
          {loading ? (
            <CameraLoader text="Loading profile..." />
          ) : (
            <form className="ep-form" onSubmit={handleSave}>
              {/* Logo Upload */}
              <div className="ep-logo-section">
                <div className="ep-logo-preview">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Studio logo" />
                  ) : (
                    <span className="ep-logo-placeholder">📷</span>
                  )}
                </div>
                <div className="ep-logo-info">
                  <h3>Studio Logo</h3>
                  <p>Upload your studio logo (max 5 MB)</p>
                  <label className="ep-logo-btn">
                    Choose File
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      hidden
                    />
                  </label>
                </div>
              </div>

              {error && <div className="ep-error">{error}</div>}
              {success && <div className="ep-success">{success}</div>}

              {/* Fields */}
              <div className="ep-fields">
                <div className="ep-field">
                  <label>Username</label>
                  <input
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                  />
                </div>
                <div className="ep-field">
                  <label>Email</label>
                  <input name="email" value={form.email} readOnly />
                </div>
                <div className="ep-field">
                  <label>Studio Name</label>
                  <input
                    name="studio_name"
                    value={form.studio_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="ep-field">
                  <label>Phone</label>
                  <input
                    name="phone_number"
                    value={form.phone_number}
                    onChange={handleChange}
                  />
                </div>
                <div className="ep-field full">
                  <label>Address</label>
                  <input
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                  />
                </div>
                <div className="ep-field">
                  <label>City</label>
                  <input
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                  />
                </div>
                <div className="ep-field">
                  <label>State</label>
                  <input
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                  />
                </div>
                <div className="ep-field">
                  <label>Country</label>
                  <input
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                  />
                </div>
                <div className="ep-field">
                  <label>Postal Code</label>
                  <input
                    name="postal_code"
                    value={form.postal_code}
                    onChange={handleChange}
                  />
                </div>
                <div className="ep-field">
                  <label>GSTIN Number</label>
                  <input
                    name="gstin"
                    value={form.gstin}
                    onChange={handleChange}
                    placeholder="e.g. 22AAAAA0000A1Z5"
                  />
                </div>
                <div className="ep-field">
                  <label>Currency</label>
                  <select
                    name="currency"
                    value={form.currency}
                    onChange={handleChange}
                    className="ep-select"
                  >
                    <option value="INR">INR (₹)</option>
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="GBP">GBP (£)</option>
                    <option value="AED">AED (د.إ)</option>
                    <option value="AUD">AUD (A$)</option>
                    <option value="CAD">CAD (C$)</option>
                    <option value="SGD">SGD (S$)</option>
                  </select>
                </div>
                <div className="ep-field full">
                  <label>Bio</label>
                  <textarea
                    name="bio"
                    value={form.bio}
                    onChange={handleChange}
                    rows={3}
                  />
                </div>
              </div>

              <button type="submit" className="ep-save-btn" disabled={saving}>
                {saving ? "Saving..." : "Save Profile"}
              </button>
            </form>
          )}
        </main>
      </div>
    </div>
  );
}

export default EditProfile;
